import { useState } from 'react';
import type React from 'react';
import { cn } from '@/lib/utils';
import type { Message, ToolResult } from '@/types';

interface MessageBubbleProps {
  message: Message;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser ? 'bg-brand' : 'bg-zinc-700'
        )}
      >
        <span className="material-icons text-lg text-white">
          {isUser ? 'person' : 'smart_toy'}
        </span>
      </div>

      {/* Message Content */}
      <div className={cn('flex-1 max-w-[80%]', isUser && 'flex flex-col items-end')}>
        <div
          className={cn(
            'px-4 py-3',
            isUser ? 'chat-message-user' : 'chat-message-assistant'
          )}
        >
          <div className="message-content">
            <MessageContent content={message.content} />
          </div>
        </div>

        {/* Tool Results */}
        {message.tool_results && message.tool_results.length > 0 && (
          <div className="mt-2 space-y-2 w-full">
            {message.tool_results.map((result, index) => (
              <ToolResultCard key={index} result={result} />
            ))}
          </div>
        )}

        {/* Timestamp */}
        <div className="mt-1 text-xs text-zinc-600">
          {new Date(message.created_at).toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
          })}
          {message.model && (
            <span className="ml-2 text-zinc-700">
              via {message.model.replace('claude-', '').replace('gpt-', '')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageContent({ content }: { content: string }) {
  // Simple markdown-like rendering
  // In production, you'd use a proper markdown library
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let inCodeBlock = false;
  let codeContent: string[] = [];

  lines.forEach((line, index) => {
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        // End code block
        elements.push(
          <pre key={index} className="chat-code-block">
            <code>{codeContent.join('\n')}</code>
          </pre>
        );
        codeContent = [];
        inCodeBlock = false;
      } else {
        // Start code block (language hint ignored for now)
        inCodeBlock = true;
      }
    } else if (inCodeBlock) {
      codeContent.push(line);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={index}>{line.slice(2)}</h1>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={index}>{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      elements.push(<h3 key={index}>{line.slice(4)}</h3>);
    } else if (line.startsWith('- ')) {
      elements.push(<li key={index}>{line.slice(2)}</li>);
    } else if (line.trim() === '') {
      elements.push(<br key={index} />);
    } else {
      // Handle inline code
      const parts = line.split(/`([^`]+)`/);
      elements.push(
        <p key={index}>
          {parts.map((part, i) =>
            i % 2 === 1 ? <code key={i}>{part}</code> : part
          )}
        </p>
      );
    }
  });

  return <>{elements}</>;
}

interface ToolResultCardProps {
  result: ToolResult;
}

function ToolResultCard({ result }: ToolResultCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getToolIcon = (name: string) => {
    if (name.includes('app')) return 'apps';
    if (name.includes('database')) return 'storage';
    if (name.includes('org')) return 'business';
    if (name.includes('project')) return 'folder';
    if (name.includes('log')) return 'description';
    return 'build';
  };

  const formatToolName = (name: string) => {
    return name
      .replace('wiserweb_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div
      className={cn(
        'tool-result',
        result.success ? 'tool-result-success' : 'tool-result-error'
      )}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="tool-result-header w-full text-left"
      >
        <span className="material-icons text-lg">
          {getToolIcon(result.tool_name)}
        </span>
        <span className="flex-1">{formatToolName(result.tool_name)}</span>
        <span
          className={cn(
            'text-xs px-2 py-0.5 rounded-full',
            result.success
              ? 'bg-green-500/20 text-green-400'
              : 'bg-red-500/20 text-red-400'
          )}
        >
          {result.success ? 'Success' : 'Failed'}
        </span>
        <span className="material-icons text-lg transition-transform">
          {isExpanded ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isExpanded && (
        <div className="mt-2 pt-2 border-t border-zinc-700/50">
          {result.error ? (
            <p className="text-sm text-red-400">{result.error}</p>
          ) : (
            <pre className="text-xs text-zinc-400 overflow-x-auto">
              {typeof result.result === 'string'
                ? result.result
                : JSON.stringify(result.result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
